import { Injectable, signal } from '@angular/core';
import {
    MessageToastItem,
    MessageToastType,
} from '@lib/components/organisms/message-toast/message-toast.types';

export interface ShowMessageToastOptions {
    type: MessageToastType;
    title: string;
    message: string;
    durationMs?: number;
}

@Injectable({ providedIn: 'root' })
export class MessageToastService {
    private readonly itemsSignal = signal<MessageToastItem[]>([]);
    private readonly timers = new Map<string, ReturnType<typeof setTimeout>>();

    readonly items = this.itemsSignal.asReadonly();

    show(options: ShowMessageToastOptions): void {
        const id = crypto.randomUUID();
        const item: MessageToastItem = {
            id,
            type: options.type,
            title: options.title,
            message: options.message,
        };

        this.itemsSignal.update((items) => [...items, item]);

        const duration = options.durationMs ?? 7000;
        const timer = setTimeout(() => this.dismiss(id), duration);
        this.timers.set(id, timer);
    }

    success(title: string, message: string, durationMs?: number): void {
        this.show({ type: 'success', title, message, durationMs });
    }

    info(title: string, message: string, durationMs?: number): void {
        this.show({ type: 'info', title, message, durationMs });
    }

    warning(title: string, message: string, durationMs?: number): void {
        this.show({ type: 'warning', title, message, durationMs });
    }

    error(title: string, message: string, durationMs?: number): void {
        this.show({ type: 'error', title, message, durationMs: durationMs ?? 9000 });
    }

    dismiss(id: string): void {
        const timer = this.timers.get(id);
        if (timer) {
            clearTimeout(timer);
            this.timers.delete(id);
        }
        this.itemsSignal.update((items) => items.filter((item) => item.id !== id));
    }
}
